import React, { VFC, useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useScreenDetector } from '@dxb-web-ui-framework/common/hooks';
import { Nullable } from '@dxb-web-ui-framework/common/types';
import { Box, makeStyles, Alert } from '@dxb-web-ui-framework/design-system';
import { useInvokeSWRService } from '@dxb-web-ui-framework/common/hooks/DataService';
import { TransferServiceImpl } from '../../../../../fundTransfer/src/domain/Transfer/usecases';
import { base64Formatter, convertBase64 } from '../../../utils';
import { mimeTypesToDownload } from '../../../utils/mimeTypesToDownload';
import { SingleFileUpload } from '../SingleFileUpload';

const useStyles = makeStyles({
  retry: {
    '&:hover': {
      cursor: 'pointer',
    },
  },
});

export type TransferUploaderProps = {
  transferService: TransferServiceImpl;
  transactionId: number;
  onRefetchData: () => void;
  isLoadingTransactionDocuments?: boolean;
};

const supportedFiles = [
  mimeTypesToDownload.bmp,
  mimeTypesToDownload.ico,
  mimeTypesToDownload.gif,
  mimeTypesToDownload.cur,
  mimeTypesToDownload.jpg,
  mimeTypesToDownload.jpeg,
  mimeTypesToDownload.jfif,
  mimeTypesToDownload.png,
  mimeTypesToDownload.xml,
  mimeTypesToDownload.tif,
  mimeTypesToDownload.tiff,
  mimeTypesToDownload.pdf,
  mimeTypesToDownload.xls,
  mimeTypesToDownload.xlsb,
  mimeTypesToDownload.xlsm,
  mimeTypesToDownload.xlsx,
  mimeTypesToDownload.svg,
  mimeTypesToDownload.webp,
  mimeTypesToDownload.apng,
];

const TransferUploader: VFC<TransferUploaderProps> = ({
  transferService,
  transactionId,
  onRefetchData,
  isLoadingTransactionDocuments,
}) => {
  const { isExtraSmallScreen: isMobile } = useScreenDetector();
  const [selectedFile, setSelectedFile] = useState<Nullable<File>>(null);
  const [fileData, setFileData] = useState<Nullable<string>>(null);
  const [selectedFileError, setSelectedFileError] = useState<string | undefined>();
  const [timestamp, setTimestamp] = useState<string>('');

  const classes = useStyles();
  const { t } = useTranslation();

  const onConvertBase64File = async (file: File) => {
    const fileDataBase64 = await convertBase64(file);
    setTimestamp(new Date().toISOString());
    setFileData(fileDataBase64);
  };

  useEffect(() => {
    if (selectedFile) {
      onConvertBase64File(selectedFile);
    }
  }, [selectedFile]);

  const shouldUploadDocument = Boolean(fileData);

  const {
    data: uploadDocumentData,
    error: uploadDocumentError,
    isLoading: isLoadingUploadDocument,
    mutate,
  } = useInvokeSWRService(
    transferService.PostDocument,
    {
      fileData: fileData ? base64Formatter(fileData) : '',
      fileName: selectedFile?.name,
      transactionId,
      fileDescription: selectedFile?.name,
      nextCallback: onRefetchData,
      meta: {
        timestamp,
      },
    },
    shouldUploadDocument,
    'transferService.PostDocument',
  );

  useEffect(() => {
    if (uploadDocumentData) {
      setSelectedFile(null);
      setFileData(null);
    }
  }, [uploadDocumentData]);

  return (
    <>
      {uploadDocumentError && (
        <Box mb={3}>
          <Alert
            icon={false}
            severity='error'
            variant='filled'
            action={
              <Box
                title={t('consolidatedTransactionsDetailsPage.Requests.SingleTransactionDocuments.Retry')}
                onClick={() => mutate && mutate()}
                className={classes.retry}>
                {t('consolidatedTransactionsDetailsPage.Requests.SingleTransactionDocuments.Retry')}
              </Box>
            }>
            {t('consolidatedTransactionsDetailsPage.Requests.SingleTransactionDocuments.Upload.Error.Message')}
          </Alert>
        </Box>
      )}
      <SingleFileUpload
        isLoading={isLoadingUploadDocument}
        isDisabled={isLoadingUploadDocument || Boolean(isLoadingTransactionDocuments)}
        name='fileName'
        supportedFiles={supportedFiles}
        description={t(
          'consolidatedTransactionDetailsPage.ConsolidatedTransactionDetailsDrawer.ConsolidatedTransactionsDetailsContent.FileUploader.Description',
        )}
        label={t(
          'consolidatedTransactionDetailsPage.ConsolidatedTransactionDetailsDrawer.ConsolidatedTransactionsDetailsContent.FileUploader.Label',
        )}
        type={t(
          'consolidatedTransactionDetailsPage.ConsolidatedTransactionDetailsDrawer.ConsolidatedTransactionsDetailsContent.FileUploader.Type',
        )}
        mt={isMobile ? 0 : 3}
        t={t}
        selectedFile={selectedFile}
        setSelectedFile={setSelectedFile}
        selectedFileError={selectedFileError}
        setSelectedFileError={setSelectedFileError}
        fileNameValidationRegex={/[a-zA-Z0-9-!@#$%^&*()_+= ]+$/}
      />
    </>
  );
};

export { TransferUploader };
